import { useCallback, useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { setNotifications } from 'store/actions/global.action';
import { selectNotifications } from 'store/selectors/global.selector';
import socketKeys from 'utilities/enums/socketKeys';
import SignalRService from 'utilities/services/SignalRService';
import useSelect from './useSelect';

const listenToAllNotificationsEvents = keyCallbackPairs => {
  Object.entries(keyCallbackPairs).forEach(([key, callback]) => {
    SignalRService.receiveMessage(key, callback);
  });
};

/**
 * This hook is responsible for managing and updating notifications in real-time. It listens to various events and updates the notifications state accordingly.
 */
const useNotifications = () => {
  const notifications = useSelect(selectNotifications);
  const dispatch = useDispatch();

  const updateNotifications = useCallback(
    newNotification => {
      let tempNotifications = { ...notifications };
      const existingNotificationIndex = notifications.data.findIndex(
        notification => notification.id === newNotification.id,
      );
      if (existingNotificationIndex !== -1) {
        // Update the existing notification
        tempNotifications.data[existingNotificationIndex] = newNotification;
      } else {
        // Add the new notification
        tempNotifications.data = [newNotification, ...tempNotifications.data];
        tempNotifications.unreadNotificationsCount += 1;
        tempNotifications.paginationData = {
          ...tempNotifications.paginationData,
          realTimeAddition: (tempNotifications.paginationData.realTimeAddition || 0) + 1,
          total: tempNotifications.paginationData.total + 1,
        };
      }
      dispatch(setNotifications(tempNotifications));
    },
    [notifications],
  );

  // Add more keyCallbackPairs here to add more events
  const keyCallbackPairs = useMemo(
    () => ({
      [socketKeys.ENTITY_ASSIGNED]: updateNotifications,
      [socketKeys.CASE_ASSIGNED]: updateNotifications,
      [socketKeys.ENTITY_CREATED]: updateNotifications,
      [socketKeys.CASE_CREATED]: updateNotifications,
      [socketKeys.RCU_RECEIVED]: updateNotifications,
      [socketKeys.KYC_REFRESH_ARRIVED]: updateNotifications,
      [socketKeys.ENTITY_ASSIGNED_NEW_ISSUE]: updateNotifications,
      [socketKeys.CASE_ASSIGNED_OM_ISSUE]: updateNotifications,
      [socketKeys.OM_EXPIRED]: updateNotifications,
      [socketKeys.MENTION_IN_COMMENT]: updateNotifications,
      [socketKeys.ENTITY_DELETED]: updateNotifications,
      [socketKeys.CASE_DELETED]: updateNotifications,
      [socketKeys.ENTITY_DELETION_FINISHED]: updateNotifications,
      [socketKeys.CASE_DELETION_FINISHED]: updateNotifications,
    }),
    [updateNotifications],
  );

  useEffect(() => {
    listenToAllNotificationsEvents(keyCallbackPairs);
  }, [keyCallbackPairs]);
};
export default useNotifications;
